/**
 * BUSINESS LAYER (BL): ARMA LA PLAYLIST [HU-04]
 * Lógica de negocio, formateo, utilerías de búsqueda/copia y wrappers unificados
 * para la consola operativa de cabina, DJ y administración de 'Arma la Playlist'.
 * 
 * @module utilities/BL/Admin/Overlays/Playlist
 */

import * as XLSX from 'xlsx';
import {
    GetSuggestions as DLGetSuggestions,
    GetActivePlaylist as DLGetActivePlaylist,
    SubmitSuggestion as DLSubmitSuggestion,
    CreateManualSong as DLCreateManualSong,
    ToggleFavorite as DLToggleFavorite,
    ToggleArchive as DLToggleArchive,
    UpdateSuggestion as DLUpdateSuggestion,
    DeleteSuggestion as DLDeleteSuggestion,
    ReorderPlaylist as DLReorderPlaylist,
    SetNowPlaying as DLSetNowPlaying,
    TogglePlayed as DLTogglePlayed,
    GetSettings as DLGetSettings,
    UpdateSettings as DLUpdateSettings,
    ToggleSubmissionActive as DLToggleSubmissionActive,
    ResetDynamic as DLResetDynamic,
    GetMetrics as DLGetMetrics,
    GetPublicConfig as DLGetPublicConfig,
    UploadPlaylistAsset as DLUploadPlaylistAsset
} from '../../../DL/Admin/Overlays/Playlist';

// ==========================================
// Utilerías de Búsqueda y Portapapeles
// ==========================================

/**
 * Copia el nombre de la pista y artista al portapapeles del sistema operativo
 * en formato "${title}${artist ? ' - ' + artist : ''}".
 * 
 * @param {string} title - Título de la pista musical.
 * @param {string} [artist=''] - Nombre del artista o grupo.
 * @returns {Promise<{success: boolean, text: string, message?: string}>}
 */
export const CopyTrackToClipboard = async (title = '', artist = '') => {
    const cleanTitle = (title || '').trim();
    const cleanArtist = (artist || '').trim();
    const trackText = `${cleanTitle}${cleanArtist ? ' - ' + cleanArtist : ''}`;

    if (!trackText) {
        return { success: false, text: '', message: 'No hay título ni artista para copiar.' };
    }

    try {
        if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            await navigator.clipboard.writeText(trackText);
            return { success: true, text: trackText, message: 'Copiado al portapapeles con éxito.' };
        } else {
            // Fallback para contextos no seguros o navegadores sin API async Clipboard
            const textArea = document.createElement('textarea');
            textArea.value = trackText;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            textArea.style.left = '-9999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const copied = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (copied) {
                return { success: true, text: trackText, message: 'Copiado al portapapeles con éxito.' };
            } else {
                return { success: false, text: trackText, message: 'No se pudo copiar al portapapeles.' };
            }
        }
    } catch (error) {
        console.error('[PlaylistBL] Error al copiar al portapapeles:', error);
        return { success: false, text: trackText, message: 'Error inesperado al copiar al portapapeles.' };
    }
};

/**
 * Genera el enlace directo para buscar la canción o artista en Spotify.
 * 
 * @param {string} title - Título de la pista.
 * @param {string} [artist=''] - Artista de la pista.
 * @returns {string} URL de búsqueda en Spotify.
 */
export const GetSpotifySearchUrl = (title = '', artist = '') => {
    const cleanTitle = (title || '').trim();
    const cleanArtist = (artist || '').trim();
    const query = cleanArtist ? `${cleanTitle} ${cleanArtist}` : cleanTitle;
    return `https://open.spotify.com/search/${encodeURIComponent(query)}`;
};

/**
 * Genera el enlace directo para buscar la canción o artista en YouTube.
 * 
 * @param {string} title - Título de la pista.
 * @param {string} [artist=''] - Artista de la pista.
 * @returns {string} URL de búsqueda en YouTube.
 */
export const GetYouTubeSearchUrl = (title = '', artist = '') => {
    const cleanTitle = (title || '').trim();
    const cleanArtist = (artist || '').trim();
    const query = cleanArtist ? `${cleanTitle} ${cleanArtist}` : cleanTitle;
    return `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
};

// ==========================================
// Wrappers de Negocio para Data Layer
// ==========================================

/**
 * Consulta la lista de sugerencias con filtros aplicados para la consola de administración.
 * 
 * @param {Object} params
 */
export const GetSuggestions = async (params = {}) => {
    try {
        const response = await DLGetSuggestions(params);
        return response;
    } catch (error) {
        return { success: false, data: [], message: 'Error inesperado en BL al obtener sugerencias.' };
    }
};

/**
 * Consulta la lista activa de canciones en cola de reproducción.
 * 
 * @param {Object} params
 */
export const GetActivePlaylist = async (params = {}) => {
    try {
        const response = await DLGetActivePlaylist(params);
        return response;
    } catch (error) {
        return { success: false, data: [], message: 'Error inesperado en BL al obtener playlist activa.' };
    }
};

/**
 * Envía una sugerencia musical de aficionado validando los campos mínimos.
 * 
 * @param {Object} params
 */
export const SubmitSuggestion = async (params = {}) => {
    try {
        if (!params.title || !params.title.trim()) {
            return { success: false, data: null, message: 'El título de la canción es obligatorio.' };
        }
        const response = await DLSubmitSuggestion(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al enviar sugerencia.' };
    }
};

/**
 * Crea una canción agregada manualmente por el DJ u operador.
 * 
 * @param {Object} params
 */
export const CreateManualSong = async (params = {}) => {
    try {
        if (!params.title || !params.title.trim()) {
            return { success: false, data: null, message: 'El título de la canción es obligatorio.' };
        }
        const response = await DLCreateManualSong(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al crear canción manual.' };
    }
};

/**
 * Alterna o establece el estado de favorito (pin) de una sugerencia.
 * 
 * @param {Object} params
 */
export const ToggleFavorite = async (params = {}) => {
    try {
        if (!params.suggestionId) {
            return { success: false, data: null, message: 'El identificador de la sugerencia es obligatorio.' };
        }
        const response = await DLToggleFavorite(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al actualizar favorito.' };
    }
};

/**
 * Alterna o establece el estado de archivado de una sugerencia.
 * 
 * @param {Object} params
 */
export const ToggleArchive = async (params = {}) => {
    try {
        if (!params.suggestionId) {
            return { success: false, data: null, message: 'El identificador de la sugerencia es obligatorio.' };
        }
        const response = await DLToggleArchive(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al actualizar archivo.' };
    }
};

/**
 * Actualiza campos y atributos de una sugerencia existente.
 * 
 * @param {Object} params
 */
export const UpdateSuggestion = async (params = {}) => {
    try {
        if (!params.suggestionId) {
            return { success: false, data: null, message: 'El identificador de la sugerencia es obligatorio.' };
        }
        const response = await DLUpdateSuggestion(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al actualizar sugerencia.' };
    }
};

/**
 * Elimina una sugerencia de la sala.
 * 
 * @param {Object} params
 */
export const DeleteSuggestion = async (params = {}) => {
    try {
        if (!params.suggestionId) {
            return { success: false, data: null, message: 'El identificador de la sugerencia es obligatorio.' };
        }
        const response = await DLDeleteSuggestion(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al eliminar sugerencia.' };
    }
};

/**
 * Reordena la cola de reproducción activa.
 * 
 * @param {Object} params
 */
export const ReorderPlaylist = async (params = {}) => {
    try {
        if (!params.orderedIds || !Array.isArray(params.orderedIds)) {
            return { success: false, message: 'La lista de IDs ordenados es inválida.' };
        }
        const response = await DLReorderPlaylist(params);
        return response;
    } catch (error) {
        return { success: false, message: 'Error inesperado en BL al reordenar la playlist.' };
    }
};

/**
 * Establece o remueve la canción en reproducción en vivo (Now Playing).
 * 
 * @param {Object} params
 */
export const SetNowPlaying = async (params = {}) => {
    try {
        const response = await DLSetNowPlaying(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al establecer Now Playing.' };
    }
};

/**
 * Alterna o actualiza el estado de reproducida (IsPlayed) de una canción.
 * 
 * @param {Object} params
 */
export const TogglePlayed = async (params = {}) => {
    try {
        if (!params.suggestionId) {
            return { success: false, data: null, message: 'El identificador de la sugerencia es obligatorio.' };
        }
        const response = await DLTogglePlayed(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al actualizar estado de reproducción.' };
    }
};

/**
 * Consulta la configuración operativa y visual completa de Arma la Playlist.
 * 
 * @param {Object} params
 */
export const GetSettings = async (params = {}) => {
    try {
        const response = await DLGetSettings(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al obtener configuración.' };
    }
};

/**
 * Sanitiza valores de celda para evitar vulnerabilidades de inyección de fórmulas en hojas de cálculo (CSV/Excel Injection).
 * Si el valor es una cadena de texto y comienza con '=', '+', '-', '@', '\t' o '\r', se prefija con apóstrofe (').
 * 
 * @param {*} val - Valor original de la celda.
 * @returns {*} Valor sanitizado apto para Excel.
 */
const sanitizeExcelCell = (val) => {
    if (val === null || val === undefined) {
        return '';
    }
    if (typeof val === 'string') {
        const forbiddenChars = ['=', '+', '-', '@', '\t', '\r'];
        const trimmed = val.trimStart();
        if (forbiddenChars.some(char => val.startsWith(char) || trimmed.startsWith(char))) {
            return `'${val}`;
        }
        return val;
    }
    return val;
};

/**
 * Formatea fechas para el reporte exportable de Excel.
 * 
 * @param {string|Date} dateVal - Fecha a formatear.
 * @returns {string} Fecha formateada en estándar local.
 */
const formatReportDate = (dateVal) => {
    if (!dateVal) return '';
    try {
        const date = new Date(dateVal);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleString('es-MX', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    } catch {
        return '';
    }
};

/**
 * Genera y descarga el reporte en formato Excel (.xlsx) con dos hojas de trabajo:
 * 1. "Peticiones y Votos"
 * 2. "Playlist Oficial"
 * 
 * @param {Array<Object>} [suggestions=[]] - Lista completa de sugerencias musicales.
 * @param {Array<Object>} [activePlaylist=[]] - Lista de pistas en la playlist activa.
 * @param {string} [roomName=''] - Nombre de la sala o estadio.
 * @returns {{ success: boolean, message: string }} Resultado de la exportación.
 */
export const ExportPlaylistToExcel = (suggestions = [], activePlaylist = [], roomName = '') => {
    try {
        const dateNow = new Date();
        const dateStr = dateNow.toISOString().replace(/[:T]/g, '_').slice(0, 19);

        // --- HOJA 1: Peticiones y Votos ---
        const sheet1Data = (suggestions || []).map((item) => {
            const isSongType = Number(item.suggestionType) === 1 || item.suggestionType === 'Song';
            const tipoStr = isSongType ? 'Canción' : 'Artista';
            const origenStr = item.isManualEntry ? 'Operador' : 'Afición';
            const aficionadoOperadorStr = item.isManualEntry
                ? (item.createdByOperatorName || 'Operador')
                : (item.fanName || 'Aficionado Anónimo');

            return {
                'ID': sanitizeExcelCell(item.id || ''),
                'Fecha': sanitizeExcelCell(formatReportDate(item.createdAtUtc || item.createdAt)),
                'Título': sanitizeExcelCell(item.title || ''),
                'Artista': sanitizeExcelCell(item.artistName || ''),
                'Tipo': tipoStr,
                'Aficionado / Operador': sanitizeExcelCell(aficionadoOperadorStr),
                'Origen': origenStr,
                'Votos': typeof item.voteCount === 'number' ? item.voteCount : 0,
                'En Playlist Activa': item.isFavorite ? 'Sí' : 'No',
                'Reproducida': item.isPlayed ? 'Sí' : 'No',
                'Archivada': item.isArchived ? 'Sí' : 'No'
            };
        });

        // --- HOJA 2: Playlist Oficial ---
        const sheet2Data = (activePlaylist || []).map((item, index) => {
            const registradoPorStr = item.isManualEntry
                ? (item.createdByOperatorName || 'Operador')
                : (item.fanName || 'Aficionado');

            return {
                'Posición': index + 1,
                'Título': sanitizeExcelCell(item.title || ''),
                'Artista': sanitizeExcelCell(item.artistName || ''),
                'Registrado Por': sanitizeExcelCell(registradoPorStr),
                'Reproducida': item.isPlayed ? 'Sí' : 'No',
                'Hora Reproducción': sanitizeExcelCell(formatReportDate(item.playedAtUtc || item.playedAt)),
                'Total Votos': typeof item.voteCount === 'number' ? item.voteCount : 0
            };
        });

        // Crear libro y hojas
        const workbook = XLSX.utils.book_new();
        const worksheet1 = XLSX.utils.json_to_sheet(sheet1Data);
        const worksheet2 = XLSX.utils.json_to_sheet(sheet2Data);

        XLSX.utils.book_append_sheet(workbook, worksheet1, 'Peticiones y Votos');
        XLSX.utils.book_append_sheet(workbook, worksheet2, 'Playlist Oficial');

        // Nombre de archivo sanitizado
        const safeRoomName = (roomName || 'Estadio').toString().trim().replace(/[^a-zA-Z0-9_-]/g, '_');
        const filename = `Reporte_ArmaLaPlaylist_${safeRoomName || 'Estadio'}_${dateStr}.xlsx`;

        XLSX.writeFile(workbook, filename);

        return { success: true, message: 'Reporte Excel exportado con éxito.' };
    } catch (error) {
        console.error('[PlaylistBL] Error al exportar a Excel:', error);
        return { success: false, message: 'Ocurrió un error al generar el reporte en Excel.' };
    }
};

/**
 * Actualiza los ajustes operativos y visuales de Arma la Playlist.
 * 
 * @param {Object} params
 */
export const UpdateSettings = async (params = {}) => {
    try {
        const roomId = params.roomId || (params.data && params.data.roomId);
        let data = params.data || { ...params };
        if (!params.data && data.roomId) {
            const { roomId: _discard, ...restData } = data;
            data = restData;
        }
        const response = await DLUpdateSettings({ roomId, data });
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al actualizar configuración.' };
    }
};

/**
 * Abre o pausa la recepción de sugerencias de aficionados.
 * 
 * @param {Object} params
 */
export const ToggleSubmissionActive = async (params = {}) => {
    try {
        const response = await DLToggleSubmissionActive(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al actualizar recepción de sugerencias.' };
    }
};

/**
 * Reinicia la dinámica en la sala con confirmación.
 * 
 * @param {Object} params
 */
export const ResetDynamic = async (params = {}) => {
    try {
        const response = await DLResetDynamic(params);
        return response;
    } catch (error) {
        return { success: false, message: 'Error inesperado en BL al reiniciar la dinámica.' };
    }
};

/**
 * Consulta las métricas y estadísticas en tiempo real de Arma la Playlist.
 * 
 * @param {Object} params
 */
export const GetMetrics = async (params = {}) => {
    try {
        const response = await DLGetMetrics(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al obtener métricas.' };
    }
};

/**
 * Consulta la configuración pública de Arma la Playlist.
 * 
 * @param {Object} params
 */
export const GetPublicConfig = async (params = {}) => {
    try {
        const response = await DLGetPublicConfig(params);
        return response;
    } catch (error) {
        return { success: false, data: null, message: 'Error inesperado en BL al obtener configuración pública.' };
    }
};

/**
 * Sube un recurso gráfico de fondo o logo para Arma la Playlist.
 * 
 * @param {FormData|File} fileOrFormData - Archivo o FormData con el archivo multimedia.
 * @param {string|null} [roomId=null] - GUID de la sala.
 */
export const UploadPlaylistAsset = async (fileOrFormData, roomId = null) => {
    try {
        if (!fileOrFormData) {
            return { success: false, url: null, data: null, message: 'No se ha seleccionado ningún archivo para subir.' };
        }
        const response = await DLUploadPlaylistAsset(fileOrFormData, roomId);
        return response;
    } catch (error) {
        return { success: false, url: null, data: null, message: 'Error inesperado en BL al subir recurso de Playlist.' };
    }
};

export default {
    CopyTrackToClipboard,
    GetSpotifySearchUrl,
    GetYouTubeSearchUrl,
    ExportPlaylistToExcel,
    GetSuggestions,
    GetActivePlaylist,
    SubmitSuggestion,
    CreateManualSong,
    ToggleFavorite,
    ToggleArchive,
    UpdateSuggestion,
    DeleteSuggestion,
    ReorderPlaylist,
    SetNowPlaying,
    TogglePlayed,
    GetSettings,
    UpdateSettings,
    ToggleSubmissionActive,
    ResetDynamic,
    GetMetrics,
    GetPublicConfig,
    UploadPlaylistAsset
};
